// Mirror of the GTM Platform container's event whitelist, plus the rules that decide
// whether an event in GA_EVENTS can actually reach GA4.
//
// Background: on 2026-07-31 `sign_up_completed` was found to have never reached GA4. The
// container's whitelist trigger listed the prefix `signup_` while the code fires
// `sign_up_completed`, so the generic tag never fired and no beacon was sent — while the
// dataLayer push itself looked perfectly healthy. Nothing in the repo could have caught that,
// because the whitelist lived only in the GTM UI. This file is that missing half, checked in.
//
// This module is deliberately free of `import.meta.env` so that scripts/check-gtm-whitelist.mjs
// can import it under plain node.

/** Platform container. The marketing site ships to GTM-TQJ27S8X and has its own, narrower list. */
export const GTM_CONTAINER_ID = 'GTM-T8RBCTTX';

/**
 * Verbatim copy of the `_re` predicate on trigger `CE - All TD Events (Whitelist)`.
 *
 * Kept as a string so the drift check can compare it byte-for-byte with the published
 * container. The live trigger has no ignore-case flag, so matching is CASE-SENSITIVE — a
 * capitalised event name will not match a lowercase prefix.
 *
 * Update this together with the container, never on its own.
 *
 * scripts/check-gtm-whitelist.mjs finds this trigger in the published container by the token
 * `agentic_ai_` and needs exactly one predicate in the whole container to contain it. Keep
 * `agentic_ai_` in this regex, and keep it out of every other trigger or exception (for example an
 * "equals agentic_ai_message_sent" trigger for a dedicated tag). Either change makes the check exit
 * 2, which CI reports as a warning only, so it would stop detecting drift without turning red.
 *
 * NOSONAR (S5843): the alternation is over Sonar's complexity limit and must stay that way.
 * This is not a regex we authored for readability — it is a copy of one that lives in GTM, and
 * check-gtm-whitelist.mjs compares it to the published container byte for byte. Simplifying it
 * would break that comparison, which is the only thing standing between us and a repeat of the
 * incident this file exists for.
 */
export const GTM_EVENT_WHITELIST_SOURCE = // NOSONAR (S5843)
  '^(login|logout|signup_|sign_up_|password_|onboarding_|complete_|getting_started|activation|search_|download_|company_|watchlist_|project_|projects_|agentic_ai_|message_|team_|pageview_|fund_create_|portfolio_co_|marketing_|capability_|insight_|footer_|page_not_found)'; // NOSONAR (S5843)

export const GTM_EVENT_WHITELIST = new RegExp(GTM_EVENT_WHITELIST_SOURCE);

/**
 * GA4's own event-name rule: must start with a letter, then letters/digits/underscores (`\w`,
 * with no unicode flag, is exactly [A-Za-z0-9_]), max 40 characters. Names outside this are
 * dropped by GA4 even when GTM forwards them, so a dotted or spaced name cannot be rescued by
 * widening the whitelist.
 */
export const GA4_EVENT_NAME_RULE = /^[A-Za-z]\w{0,39}$/;

/**
 * Data-layer keys that pushEvent keeps in GTM's data model for the whole session. Every other
 * key is unset right after its own event. Tags may read these on any event, including
 * GTM's own (history change, consent_status), so they count as pushed everywhere.
 */
export const DATA_LAYER_SESSION_KEYS = ['user_id', 'user_company_type'];

// --- Data-layer keys the GA4 tags read ---------------------------------------------------------
//
// Verbatim copy of what the container's active GA4 event tags read from the data layer, as
// scripts/gtmContainer.mjs derives it from the published gtm.js (V34). These are data-layer keys,
// not GA4 parameter names: the repo cannot see the GA4 registrations, but it can see whether the
// app pushes the key a tag reads. That is how four PROD report columns stayed empty unnoticed.
//
// Update together with the container, never on its own, like the whitelist regex above. The
// nightly drift check compares these with the published container, and
// __tests__/gtmFieldContract.test.js checks them against the keys the app actually pushes.

/**
 * Keys read by "GA4 - Generic Event (All TD Events)", the tag behind the whitelist trigger.
 * pushEvent also clears these before every event, so a key another script left in the data model
 * (Usercentrics' `action`) is not sent along. Keep it complete for that reason too.
 */
export const GTM_GENERIC_TAG_KEYS = [
  'action',
  'days_since_signup',
  'error_type',
  'feedback_length',
  'filters_used',
  'from_tab',
  'input_method',
  'message_length',
  'query_length',
  'reading_duration_seconds',
  'reason',
  'referral_source',
  'response_duration_ms',
  'result_type',
  'search_profile',
  'session_duration_seconds',
  'source',
  'source_message_id',
  'step_name',
  'stop_latency_ms',
  'to_tab',
  'tokens_consumed',
  'total_filters_applied',
  'user_company_type',
  'user_id'
];

/** The generic tag's "unless" condition: events that have a dedicated tag instead (V32). */
export const GTM_GENERIC_TAG_EXCLUSION_SOURCES = [
  '^(portfolio_co_|pageview_|login$|download_search$|complete_company_profile$)'
];

/**
 * Keys read by the dedicated GA4 tags, per event that fires them. Tags that only read session keys
 * and fire on GTM's own events or a regex (the page views) are left out: they cannot miss a key.
 */
export const GTM_DEDICATED_TAG_KEYS = {
  complete_company_profile: ['Country', 'Tagline', 'cType'],
  download_search: ['user_id'],
  login: ['method', 'user_id'],
  outreach_search: ['user_id'],
  portfolio_co_create_complete: [
    'EBITDA_margin',
    'EBITDA_range_max',
    'EBITDA_range_min',
    'EBIT_margin',
    'EBIT_range_max',
    'EBIT_range_min',
    'enterprise_value_range_max',
    'enterprise_value_range_min',
    'equity_stake',
    'equity_value_range_max',
    'equity_value_range_min',
    'geographies',
    'industries',
    'portfolio_co_name',
    'portfolio_company_EBITDA',
    'portfolio_company_description',
    'portfolio_company_revenue',
    'revenue_range_max',
    'revenue_range_min',
    'situation',
    'user_id'
  ],
  portfolio_co_financials_complete: [
    'EBITDA_margin',
    'EBITDA_range_max',
    'EBITDA_range_min',
    'EBIT_margin',
    'EBIT_range_max',
    'EBIT_range_min',
    'portfolio_co_name',
    'revenue_range_max',
    'revenue_range_min',
    'user_id'
  ],
  portfolio_co_geoindustries_complete: [
    'geographies',
    'industries',
    'portfolio_co_name',
    'user_id'
  ],
  portfolio_co_information_complete: [
    'portfolio_co_name',
    'portfolio_company_EBITDA',
    'portfolio_company_description',
    'portfolio_company_revenue',
    'user_id'
  ],
  portfolio_co_transaction_complete: ['equity_stake', 'portfolio_co_name', 'situation', 'user_id'],
  portfolio_co_valuation_complete: [
    'enterprise_value_range_max',
    'enterprise_value_range_min',
    'equity_value_range_max',
    'equity_value_range_min',
    'portfolio_co_name',
    'user_id'
  ]
};

// --- Known gaps between the app and the tags -------------------------------------------------
//
// Both lists must match reality exactly: the contract test fails when a gap appears that is not
// listed here, and when a listed gap has been closed, so neither can go stale.

/** Generic-tag keys that no app event pushes, so their GA4 columns can only ever be empty. */
export const GTM_KEYS_NOT_PUSHED = {
  referral_source: 'The app pushes ref_source instead; GTM V35 re-points this row.',
  query_length: 'No event has ever pushed it; the GTM row can go.',
  reading_duration_seconds: 'No event has ever pushed it; the GTM row can go.',
  tokens_consumed: 'No longer pushed: it was always empty. The GTM row can go.',
  days_since_signup: 'No longer pushed: it was always empty. The GTM row can go.',
  stop_latency_ms: 'No longer pushed: it was always "0". The GTM row can go.'
};

/**
 * Keys the app pushes that no GA4 tag reads, recorded as found against container V34. Whether
 * each should reach GA4 is a product decision. The list exists so that a NEW unforwarded key
 * fails the contract test instead of silently vanishing, and it shrinks when GTM starts reading
 * one.
 */
export const KEYS_NOT_FORWARDED_TO_GA4 = [
  // Waiting for GTM V35.
  'ref_source',
  'result_count',
  'search_type',
  // Identifiers.
  'company_id',
  'conversation_id',
  'project_id',
  'request_id',
  'result_id',
  'search_id',
  // Fund wizard: its dedicated tags are paused and the generic tag reads none of these.
  'EBITDA_margin',
  'EBITDA_max',
  'EBITDA_min',
  'EBIT_margin',
  'EBIT_max',
  'EBIT_min',
  'Enterprise_VR_max',
  'Enterprise_VR_min',
  'Equity_Stake',
  'Equity_VR_max',
  'Equity_VR_min',
  'Fund_Volume',
  'Fund_vintage',
  'Geographies',
  'Industries',
  'Revenue_Range_max',
  'Revenue_Range_min',
  'Situation',
  'fund_name',
  // Search criteria, counts and result position.
  'advanced_criteria_expanded',
  'cagr_max',
  'cagr_min',
  'country_count',
  'ebit_margin_max',
  'ebit_margin_min',
  'employees_max',
  'employees_min',
  'from_type',
  'has_results',
  'industries_select_count',
  'industry_codes',
  'industry_keyword_count',
  'industry_sections',
  'listing_status_count',
  'ownership_basis',
  'product_portfolio_keyword_count',
  'result_position',
  'sales_max',
  'sales_min',
  'shareholder_structure_count',
  'situation_count',
  'to_type',
  'value_chain_keyword_count',
  // Sign-up and company type (user_company_type carries the company type to GA4).
  'cType',
  'company_type',
  'error_field',
  'has_phone',
  'signup_method',
  // Other counts and details.
  'archived_count',
  'item_count',
  'open_request_count',
  'recipient_count',
  'resolved_request_count',
  'saved_count',
  'search_reason',
  'tokens_remaining_at_block',
  // pageview_start_page: its helper has no live caller and its tag is paused.
  'funds',
  'portfolio_companies'
];

/**
 * Events deliberately left out of the whitelist because the feature that fires them is
 * switched off in every environment GA4 tracks (DEV is untracked by design), so whitelisting
 * them would add container rows and GA4 event names for traffic that cannot arrive.
 *
 * `flag` / `offValue` are not documentation — eventNames.test.js reads .env.prod and fails if
 * a flag no longer holds `offValue`. Turning one of these features on in production is
 * therefore a CI failure until the GTM whitelist is widened to match, which is precisely the
 * step that was missed in the original incident.
 */
export const EVENTS_NOT_SENT_TO_GA4 = {
  outreach_search: { flag: 'VITE_DISABLE_OUTREACH', offValue: 'true' },
  outreach_started: { flag: 'VITE_DISABLE_OUTREACH', offValue: 'true' },
  outreach_message_sent: { flag: 'VITE_DISABLE_OUTREACH', offValue: 'true' },

  agent_enrichment_options_shown: { flag: 'VITE_AGENT_ENRICHMENT_ENABLED', offValue: 'false' },
  agent_enrichment_option_selected: { flag: 'VITE_AGENT_ENRICHMENT_ENABLED', offValue: 'false' },
  agent_enrichment_reveal_confirm_shown: {
    flag: 'VITE_AGENT_ENRICHMENT_ENABLED',
    offValue: 'false'
  },
  agent_enrichment_reveal_confirmed: { flag: 'VITE_AGENT_ENRICHMENT_ENABLED', offValue: 'false' },
  agent_enrichment_reveal_cancelled: { flag: 'VITE_AGENT_ENRICHMENT_ENABLED', offValue: 'false' },
  agent_enrichment_reveal_job_started: { flag: 'VITE_AGENT_ENRICHMENT_ENABLED', offValue: 'false' },
  agent_enrichment_reveal_job_completed: {
    flag: 'VITE_AGENT_ENRICHMENT_ENABLED',
    offValue: 'false'
  }
};
